/**
 * The scopers widgets used before `scoper: 2`. Kept exactly as they were so
 * existing widgets render the same; previews use them too so they match the
 * live page. Known quirks are intentional here (see scopeCss.ts for the fixed
 * behavior):
 * - custom widget: the rule right after an @import stays unscoped, "body .x"
 *   becomes ".scope body .x" (matches nothing), braces in strings break parsing;
 * - AI widget: wraps everything in CSS nesting, which silently drops :root,
 *   body, @keyframes, @font-face and @import.
 */

const CUSTOM_BASE = 'custom-widget-root';
const AI_BASE = 'ai-widget-root';

/** Custom widget scoper, unchanged from CustomWidget.tsx. */
export const scopeCssLegacyCustom = (css: string, scopeClass: string): string => {
  const trimmed = css.trim();
  if (!trimmed) {
    return '';
  }

  // If the CSS already uses a scope class pattern, replace it
  if (trimmed.includes(`.${CUSTOM_BASE}`)) {
    const regex = new RegExp(`\\.${CUSTOM_BASE}[a-zA-Z0-9-_]*`, 'g');
    return trimmed.replace(regex, `.${scopeClass}`);
  }

  // First, strip CSS comments to avoid them breaking the selector parsing
  const stripComments = (str: string): string => {
    return str.replace(/\/\*[\s\S]*?\*\//g, '');
  };

  const cssWithoutComments = stripComments(trimmed);

  // Helper to prefix a selector
  const prefixSelector = (selector: string): string => {
    return selector.split(',').map(s => {
      const sel = s.trim();
      if (!sel) return '';
      // Don't prefix :root, html, body - convert them to scope class
      if (sel === ':root' || sel === 'html' || sel === 'body') {
        return `.${scopeClass}`;
      }
      return `.${scopeClass} ${sel}`;
    }).filter(Boolean).join(', ');
  };

  // Process CSS to prefix all selectors with scope class
  // This handles normal rules, @media queries, @keyframes, etc.
  const result: string[] = [];
  let currentBlock = '';
  let braceCount = 0;
  let inAtRule = false;
  let atRuleName = '';

  for (let i = 0; i < cssWithoutComments.length; i++) {
    const char = cssWithoutComments[i];
    currentBlock += char;

    if (char === '{') {
      braceCount++;
      const content = currentBlock.slice(0, -1).trim();

      if (braceCount === 1) {
        // Top-level block
        if (content.startsWith('@')) {
          // At-rule like @media, @keyframes, @font-face
          inAtRule = true;
          atRuleName = content.split(/[\s({]/)[0].toLowerCase();
          result.push(content + ' {');
        } else {
          // Regular selector - prefix it
          result.push(prefixSelector(content) + ' {');
        }
        currentBlock = '';
      } else if (braceCount === 2 && inAtRule) {
        // Inside an at-rule
        if (atRuleName === '@keyframes' || atRuleName === '@-webkit-keyframes') {
          // Keyframe selectors (0%, 50%, from, to) - don't prefix
          result.push(content + ' {');
        } else if (atRuleName === '@font-face') {
          // Font-face has no selectors inside
          result.push(content + ' {');
        } else {
          // @media, @supports - prefix the selector
          result.push(prefixSelector(content) + ' {');
        }
        currentBlock = '';
      } else {
        // Deeper nesting (shouldn't happen often in CSS)
        result.push(currentBlock);
        currentBlock = '';
      }
    } else if (char === '}') {
      braceCount--;
      result.push(currentBlock);
      currentBlock = '';
      if (braceCount === 0) {
        inAtRule = false;
        atRuleName = '';
      }
    }
  }

  if (currentBlock.trim()) {
    result.push(currentBlock);
  }

  return result.join('');
};

/** AI widget scoper, unchanged from AiGeneratedWidget.tsx. */
export const scopeCssLegacyAi = (css: string, scopeClass: string): string => {
  const trimmed = css.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.includes(`.${AI_BASE}`)) {
    const regex = new RegExp(`\\.${AI_BASE}`, 'g');
    return trimmed.replace(regex, `.${scopeClass}`);
  }
  return `.${scopeClass} {\n${trimmed}\n}`;
};
